import { FormEvent, useState } from 'react';
import api from '../../services/api';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { userUpdated } from '../../features/auth/authSlice';
import { Card, ModuleHero, Notice, Spinner, StatusPill } from '../../components/ui';
import { errorMessage } from '../../lib/http';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, FIELD, FIELD_STYLE, LABEL } from '../../lib/ui';

type Flash = { tone: 'green' | 'rose'; text: string } | null;

export default function Account() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const [profile, setProfile] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileFlash, setProfileFlash] = useState<Flash>(null);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [pwFlash, setPwFlash] = useState<Flash>(null);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileFlash(null);
    try {
      const { data } = await api.patch('/auth/profile', profile);
      dispatch(userUpdated({ name: data.user.name, phone: data.user.phone }));
      setProfileFlash({ tone: 'green', text: 'Profile updated.' });
    } catch (err) {
      setProfileFlash({ tone: 'rose', text: errorMessage(err) });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwFlash(null);
    if (pw.newPassword !== pw.confirm) return setPwFlash({ tone: 'rose', text: 'The new passwords do not match.' });
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      setPwFlash({ tone: 'green', text: 'Password changed.' });
    } catch (err) {
      setPwFlash({ tone: 'rose', text: errorMessage(err) });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div>
      <ModuleHero
        eyebrow="Account"
        title="Account settings"
        description="Your personal sign-in details. Company details live in Company profile."
        actions={<StatusPill label="Customer" tone="green" />}
      />
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Profile">
          <form onSubmit={saveProfile} className="space-y-3">
            <div>
              <label className={LABEL}>Email</label>
              <input value={user?.email || ''} readOnly className={`${FIELD} opacity-70`} style={FIELD_STYLE} />
            </div>
            <div>
              <label className={LABEL}>Full name</label>
              <input required minLength={2} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={FIELD} style={FIELD_STYLE} />
            </div>
            <div>
              <label className={LABEL}>Phone</label>
              <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={FIELD} style={FIELD_STYLE} />
            </div>
            <button type="submit" disabled={savingProfile} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
              {savingProfile && <Spinner />} Save profile
            </button>
            {profileFlash && <Notice tone={profileFlash.tone}>{profileFlash.text}</Notice>}
          </form>
        </Card>
        <Card title="Change password">
          <form onSubmit={savePassword} className="space-y-3">
            <input type="email" autoComplete="username" value={user?.email || ''} readOnly hidden />
            <div>
              <label className={LABEL}>Current password</label>
              <input required type="password" autoComplete="current-password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} className={FIELD} style={FIELD_STYLE} />
            </div>
            <div>
              <label className={LABEL}>New password</label>
              <input required type="password" autoComplete="new-password" minLength={10} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} className={FIELD} style={FIELD_STYLE} />
              <p className="mt-1 text-[11px] text-[#c084a0]">At least 10 characters, with a letter and a number.</p>
            </div>
            <div>
              <label className={LABEL}>Confirm new password</label>
              <input required type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={FIELD} style={FIELD_STYLE} />
            </div>
            <button type="submit" disabled={savingPw} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
              {savingPw && <Spinner />} Update password
            </button>
            {pwFlash && <Notice tone={pwFlash.tone}>{pwFlash.text}</Notice>}
          </form>
        </Card>
      </div>
    </div>
  );
}
